import {getAuthenticatedThemisClient, LinkDocumentRequestView} from '@modifi/onboarding-api-clients'
import {Reducer, updateFactory} from '@modifi/redux'
import {DocumentTypeVal} from '@modifi/enums'
import {
  aAppendDocumentToCompanyInLocalStore,
  aFetchCompanyDocuments,
  aRemoveDocumentFromCompanyInLocalStore,
} from './collection'
import {NestedError} from '../../utils'

const apiClient = getAuthenticatedThemisClient()

export type UpdateCompanyDocumentsChanges = {
  added: {fileId: string; documentType: DocumentTypeVal}[]
  removed: string[]
  modifications: {fileId: string; [k: string]: any}[]
}

const sendAttachRequest = (document: LinkDocumentRequestView): Promise<void> =>
  apiClient.CompanyDocuments.attachDocument({
    body: document,
  }).then(() => undefined)
const sendDetachRequest = (externalFileId: string): Promise<void> =>
  apiClient.CompanyDocuments.detachDocument({externalFileId}).then(() => undefined)

const {selectors, reducer, actions} = updateFactory({
  actionNamePrefix: 'data/company-documents/collection',
  rootSelector: state => state?.data?.companyDocuments?.update,
  submitRequest:
    (a, changes: UpdateCompanyDocumentsChanges) =>
    async (dispatch): Promise<void> => {
      dispatch(a.pendingAction())
      try {
        await Promise.all(
          (changes?.added || []).map(async file => {
            await sendAttachRequest(file)
            dispatch(aAppendDocumentToCompanyInLocalStore(file))
          })
        )
        await Promise.all(
          (changes.removed || []).map(async fileId => {
            await sendDetachRequest(fileId)
            dispatch(aRemoveDocumentFromCompanyInLocalStore(fileId))
          })
        )
        if (changes.modifications?.length > 0) {
          // eslint-disable-next-line no-console
          console.warn('Updating files is currently not supported')
        }
      } catch (e: any) {
        dispatch(a.errorAction(e))
        throw new NestedError(
          `Error while calling CompanyDocuments.attachDocument for added docs: ${JSON.stringify(
            changes?.added
          )} or CompanyDocuments.detachDocument endpoint for removed docs: ${JSON.stringify(
            changes?.removed
          )}`,
          e
        )
      }
      try {
        await dispatch(aFetchCompanyDocuments())
      } catch (e) {
        // ignore
      }
      dispatch(a.successAction())
    },
})

export default reducer as Reducer

export const {
  selectUpdatePending: selectUpdateCompanyDocumentsRequestPending,
  selectUpdateHasError: selectUpdateCompanyDocumentsRequestHasError,
  selectUpdateErrorMessage: selectUpdateCompanyDocumentsRequestErrorMessage,
  selectUpdateErrorCode: selectUpdateCompanyDocumentsRequestErrorCode,
} = selectors

export const {aSubmit: aUpdateCompanyDocuments} = actions
